import React, { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import {
  getSummaryInfoForJob,
  createStandardVarianceChecking,
  updateVarianceCheckingStatus,
  createNextStandardVarianceChecking,
} from '../../api'
import FinalRun from '../../components/FinalRun'
import VarianceListModal from '../../components/JobDetails/VarianceListModal'
import VarianceListPreModal from '../../components/JobDetails/VarianceListPreModal'
import MainHeader from '../../components/MainHeader'
import MainView from '../../components/MainView'
import OrderInfo from '../../components/OrderInfo'
import OrdersList from '../../components/OrdersList'
import {
  fetchAllEmployees,
  fetchVarianceCheckingPartNumBin,
  fetchVarianceCheckingUserAssignments,
} from '../../store/actions'

/** Standardizes raw API run data **/
const convertSummaryInfo = (data) => {
  return data.data.map((item) => ({
    ...item,
    countingMode: item.countingMode === 'bin_centric' ? 'Bin' : 'Part',
  }))
}

const VarianceProgress = (props) => {
  const {
    employeeList,
    fetchAllEmployees,
    fetchVarianceCheckingPartNumBin,
    fetchVarianceCheckingUserAssignments,
    match,
  } = props

  const jobId = match?.params?.jobId

  // ----------------------------
  // State
  // ----------------------------
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [usersData, setUsersData] = useState(null)
  const [orders, setOrders] = useState([])
  const [varianceData, setVarianceData] = useState(null)
  const [isVarianceListPreModalOpen, setIsVarianceListPreModalOpen] = useState(false)
  const [isVarianceListModalOpen, setIsVarianceListModalOpen] = useState(false)
  const [isVarianceListButtonDisabled, setIsVarianceListButtonDisabled] = useState(true)
  const [isStatusActive, setIsStatusActive] = useState('active')

  const currentRunName =
    orders.length === 0
      ? 'New Variance'
      : `Variance Run #${orders.length} (${isStatusActive === 'active' ? 'In Progress' : 'Completed'})`

  // ----------------------------
  // Refresh helpers (ORDER MATTERS)
  // ----------------------------

  const refreshVariance = useCallback(async () => {
    setIsVarianceListButtonDisabled(true)

    try {
      const [varianceResp, usersResp] = await Promise.all([
        fetchVarianceCheckingPartNumBin(jobId),
        fetchVarianceCheckingUserAssignments(jobId),
      ])

      const newVarianceData = varianceResp?.data ?? varianceResp
      const newUsersData = usersResp?.data ?? usersResp

      setVarianceData(newVarianceData)
      setUsersData(newUsersData)

      setIsVarianceListButtonDisabled(false)
      return newVarianceData
    } catch (error) {
      console.error('Refresh failed:', error)
      setIsVarianceListButtonDisabled(false)
    }
  }, [jobId, fetchVarianceCheckingPartNumBin, fetchVarianceCheckingUserAssignments])

  const refreshOrders = useCallback(async () => {
    try {
      const resp = await getSummaryInfoForJob(jobId)
      const updatedOrders = convertSummaryInfo(resp.data)

      setOrders(updatedOrders)

      const last = updatedOrders.length > 0 ? updatedOrders[updatedOrders.length - 1] : null
      setSelectedOrder(last)

      return { updatedOrders, last }
    } catch (error) {
      console.error('refreshOrders failed:', error)
      return { updatedOrders: [], last: null }
    }
  }, [jobId])

  const waitForNewRun = useCallback(
    async (prevLastRunId) => {
      for (let i = 0; i < 10; i++) {
        const { last } = await refreshOrders()

        if (last?.id && last.id !== prevLastRunId) {
          return last
        }

        await new Promise((r) => setTimeout(r, 500))
      }
      return null
    },
    [refreshOrders]
  )

  // This MUST come after refreshOrders/refreshVariance
  const onOrderStatusChange = useCallback(
    async (orderId, status) => {
      try {
        await refreshOrders()
        await refreshVariance()
      } catch (e) {
        console.error('onOrderStatusChange handler failed:', e)
      }
    },
    [refreshOrders, refreshVariance]
  )

  // ----------------------------
  // Effects
  // ----------------------------

  useEffect(() => {
    if (orders.length > 0) {
      setIsStatusActive(orders[orders.length - 1].status)
    }
  }, [orders])

  useEffect(() => {
    if (!employeeList.length) fetchAllEmployees()

    const initLoad = async () => {
      const { data } = await getSummaryInfoForJob(jobId)
      const currentOrders = convertSummaryInfo(data)
      setOrders(currentOrders)

      if (currentOrders.length === 0) {
        setIsVarianceListPreModalOpen(true)
      } else {
        setSelectedOrder(currentOrders[currentOrders.length - 1])
        refreshVariance()
      }
    }

    initLoad()
  }, [jobId, employeeList.length, fetchAllEmployees, refreshVariance])

  // ----------------------------
  // Handlers
  // ----------------------------

  const selectCountingMode = async (countingMode, numberOfUsers) => {
    setIsVarianceListButtonDisabled(true)

    try {
      const prevLastRunId = orders?.length ? orders[orders.length - 1].id : null

      if (prevLastRunId) {
        await updateVarianceCheckingStatus(prevLastRunId, 'completed')

        await createNextStandardVarianceChecking(jobId, {
          num_of_users: numberOfUsers,
          part_ids: [],
        })
      } else {
        await createStandardVarianceChecking(jobId, {
          num_of_users: numberOfUsers,
          part_ids: [],
        })
      }

      const newRun = await waitForNewRun(prevLastRunId)

      if (!newRun) {
        await refreshOrders()
      }

      await refreshVariance()

      setIsVarianceListPreModalOpen(false)
      setIsVarianceListModalOpen(true)
    } catch (error) {
      console.error('Waterfall transition failed:', error)
      alert('Waterfall transition failed. Check console logs for details.')
    } finally {
      setIsVarianceListButtonDisabled(false)
    }
  }

  const handleCompleteJob = async () => {
    if (!window.confirm('Mark this variance run as completed? This will unlock the next waterfall step.')) {
      return
    }

    try {
      setIsVarianceListButtonDisabled(true)

      const currentRunId = orders?.[orders.length - 1]?.id

      if (!currentRunId) {
        alert('No active variance run found to complete.')
        return
      }

      await updateVarianceCheckingStatus(currentRunId, 'completed')

      await refreshOrders()
      await refreshVariance()
    } catch (err) {
      console.error('Failed to complete variance run:', err)
      alert('Failed to complete the variance run. Check console for details.')
    } finally {
      setIsVarianceListButtonDisabled(false)
    }
  }

  const toggleVarianceListModal = async (success) => {
    setIsVarianceListModalOpen(false)

    if (!success) return

    await Promise.all([refreshVariance(), refreshOrders()])
  }

  // ----------------------------
  // Render
  // ----------------------------

  return (
    <div className="variance-progress">
      <MainHeader title={currentRunName} backLink={true} />
      <MainView className="variance-progress__body">
        <OrdersList
          onOrderSelect={(id, order) => setSelectedOrder(order)}
          orders={orders}
          jobId={jobId}
          selectedOrder={selectedOrder?.id}
          onOrderStatusChange={onOrderStatusChange}
        />

        <div className="variance-progress__controls">
          {orders.length === 0 ? (
            <button className="btn btn--primary" onClick={() => setIsVarianceListPreModalOpen(true)}>
              Start Initial Variance Run
            </button>
          ) : isStatusActive === 'active' || isStatusActive === 'created' ? (
            <button
              className="btn btn--primary"
              onClick={() => setIsVarianceListModalOpen(true)}
              disabled={isVarianceListButtonDisabled}
            >
              Assign Users to Run #{orders.length}
            </button>
          ) : (
            <div
              className="waterfall-actions"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '8px',
                padding: '10px 12px',
                border: '1px solid #e6e6e6',
                borderRadius: '6px',
                background: '#fff',
                width: 'fit-content',     // key: stops full-width stretching
                maxWidth: '100%',         // prevents overflow on small screens
              }}
            >
              <button
                className="btn btn--secondary"
                onClick={() => setIsVarianceListPreModalOpen(true)}
                style={{ whiteSpace: 'nowrap' }}
              >
                Start Next Run (#{orders.length + 1})
              </button>

              <button
                className="btn btn--outline-danger"
                onClick={handleCompleteJob}
                style={{ whiteSpace: 'nowrap' }}
              >
                Finalize & Complete Entire Job
              </button>

              <span
                style={{
                  marginLeft: '6px',
                  fontSize: '0.85rem',
                  color: '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                Next run only includes mismatches.
              </span>
            </div>
          )}

          {orders.length > 0 && (
            <div
              className="variance-progress__summary"
              style={{ marginTop: '40px', paddingTop: '20px', borderTop: '2px dashed #eee' }}
            >
              <h3 style={{ marginBottom: '15px', color: '#333' }}>Cumulative Summary</h3>
              <FinalRun
                orders={orders}
                variance={true}
                varianceData={varianceData}
                usersData={usersData}
                jobId={jobId}
                refreshVariance={refreshVariance}
                refreshOrders={refreshOrders}
                waitForNewRun={waitForNewRun}
              />
            </div>
          )}
        </div>

        {isVarianceListPreModalOpen && (
          <VarianceListPreModal onClose={() => setIsVarianceListPreModalOpen(false)} onSelect={selectCountingMode} />
        )}

        {isVarianceListModalOpen && (
          <VarianceListModal onClose={toggleVarianceListModal} varianceData={varianceData} usersData={usersData} />
        )}

        {selectedOrder && (
          <OrderInfo
            order={selectedOrder}
            jobId={jobId}
            refreshOrders={refreshOrders}
            refreshVariance={refreshVariance}
            waitForNewRun={waitForNewRun}
            orders={orders}
          />
        )}
      </MainView>
    </div>
  )
}

const mapStateToProps = ({ employees: { employeeList } }) => ({ employeeList })
const mapDispatchToProps = {
  fetchAllEmployees,
  fetchVarianceCheckingPartNumBin,
  fetchVarianceCheckingUserAssignments,
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(VarianceProgress))
